import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

type TimeParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const gradients = [
  { bg: 'linear-gradient(135deg, rgba(255,147,74,0.25), rgba(255,84,117,0.4))', shadow: 'rgba(255,147,74,0.35)' },
  { bg: 'linear-gradient(135deg, rgba(0,188,212,0.25), rgba(0,77,255,0.35))', shadow: 'rgba(0,188,212,0.35)' },
  { bg: 'linear-gradient(135deg, rgba(140,82,255,0.25), rgba(255,0,153,0.35))', shadow: 'rgba(140,82,255,0.35)' },
  { bg: 'linear-gradient(135deg, rgba(0,255,188,0.2), rgba(0,128,255,0.35))', shadow: 'rgba(0,128,255,0.3)' }
];

const pad = (value: number) => value.toString().padStart(2, '0');

const computeParts = (endTime: number): TimeParts => {
  let diff = Math.max(0, Math.floor(endTime - Date.now() / 1000));
  const days = Math.floor(diff / 86400);
  diff %= 86400;
  const hours = Math.floor(diff / 3600);
  diff %= 3600;
  const minutes = Math.floor(diff / 60);
  const seconds = diff % 60;
  return { days, hours, minutes, seconds };
};

function CountDownTimer({ endTime }: { endTime: number }) {
  const [parts, setParts] = useState<TimeParts>(() => computeParts(endTime));

  useEffect(() => {
    setParts(computeParts(endTime));
    const timer = window.setInterval(() => {
      setParts(computeParts(endTime));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [endTime]);

  const segments = useMemo(
    () => [
      { label: 'Tage', value: pad(parts.days) },
      { label: 'Stunden', value: pad(parts.hours) },
      { label: 'Minuten', value: pad(parts.minutes) },
      { label: 'Sekunden', value: pad(parts.seconds) }
    ],
    [parts.days, parts.hours, parts.minutes, parts.seconds]
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          width: '100%',
          maxWidth: 520,
          justifyContent: 'space-between',
          '@keyframes glowPulse': {
            '0%': { boxShadow: '0 0 12px rgba(255,255,255,0.08)' },
            '50%': { boxShadow: '0 0 30px rgba(255,255,255,0.25)' },
            '100%': { boxShadow: '0 0 12px rgba(255,255,255,0.08)' }
          }
        }}
      >
        {segments.map((segment, idx) => (
          <Box
            key={segment.label}
            sx={{
              flex: 1,
              minWidth: { xs: 52, sm: 64 },
              borderRadius: 3,
              background: gradients[idx % gradients.length].bg,
              padding: { xs: '10px 6px', sm: '16px 12px' },
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: `0 15px 35px ${gradients[idx % gradients.length].shadow}`,
              textAlign: 'center',
              animation: 'glowPulse 6s ease-in-out infinite',
              backdropFilter: 'blur(6px)'
            }}
          >
            <Typography
              variant="h5"
              fontFamily="'Space Grotesk', 'Roboto', sans-serif"
              sx={{ color: '#fff', fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.1 }}
            >
              {segment.value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.75)',
                letterSpacing: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                display: 'block',
                mt: 0.5,
                whiteSpace: 'nowrap'
              }}
            >
              {segment.label.toUpperCase()}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default CountDownTimer;
